import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { fetchProductById, updateProduct } from "../store/actions/productActions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { ArrowLeft, Package, Edit } from "lucide-react";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const PageWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
  cursor: pointer;
  &:hover { color: ${({ theme }) => theme.colors?.primary?.DEFAULT || "#7c3aed"}; }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
`;

const ThumbWrap = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors?.background?.secondary || "#f3f4f6"};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const HeaderMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const EntityTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.text?.heading || "#111827"};
  line-height: 1.3;
`;

const EntityMeta = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ $status }) => ($status === "in_stock" ? "#dcfce7" : $status === "low_stock" ? "#fef9c3" : "#fee2e2")};
  color: ${({ $status }) => ($status === "in_stock" ? "#166534" : $status === "low_stock" ? "#854d0e" : "#991b1b")};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryItem = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
  background: ${({ theme }) => theme.colors?.background?.primary || "#fff"};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.text?.heading || "#111827"};
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  margin-bottom: 32px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled(Card)`
  padding: 20px 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
  &:last-child { border-bottom: none; }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors?.text?.body || "#374151"};
  text-align: right;
  word-break: break-word;
`;

const DescriptionBlock = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors?.text?.body || "#4b5563"};
  line-height: 1.6;
  white-space: pre-wrap;
`;

export default function ProductDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { product: productFromState, loading, error } = useSelector((state) => state.product);
  const [stockEdit, setStockEdit] = useState(null);
  const [statusEdit, setStatusEdit] = useState(null);
  const [savingStock, setSavingStock] = useState(false);
  const user = useSelector((state) => state.auth?.user);
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [rates, setRates] = useState(null);

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r?.rates || null)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    if (productId) dispatch(fetchProductById(productId));
  }, [dispatch, productId]);

  const product = productFromState || null;
  useEffect(() => {
    if (product) {
      setStockEdit(product.stock ?? 0);
      setStatusEdit(product.status || "in_stock");
    }
  }, [product?._id, product?.stock, product?.status]);

  const variants = product?.variants || [];
  const attributes = product?.attributes && typeof product.attributes === "object" ? product.attributes : {};
  const baseCurrency = "USD";
  const fmt = (v) => (v != null && typeof v === "number" ? (rates ? formatCurrency(ratesService.convert(v, baseCurrency, currency, rates), currency) : formatCurrency(v, "USD")) : "—");
  const salesHistory = product?.salesHistory || [];
  const imageUrl = product?.image || product?.imageUrl || "";

  async function handleSaveStock() {
    if (!product || stockEdit === null) return;
    setSavingStock(true);
    await dispatch(updateProduct(product._id, { stock: Number(stockEdit) || 0, status: statusEdit }));
    setSavingStock(false);
  }

  if (loading && !product) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/products")}><ArrowLeft size={16} /> Back to Products</Button>
        <p style={{ color: "#6b7280" }}>Loading product...</p>
      </div>
    );
  }
  if (error && !product) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/products")}><ArrowLeft size={16} /> Back to Products</Button>
        <p style={{ color: "#dc2626" }}>{error}</p>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/products")}><ArrowLeft size={16} /> Back to Products</Button>
        <p style={{ color: "#6b7280" }}>Product not found.</p>
      </div>
    );
  }

  const statusDisplay = product.status || (product.stock > 0 ? "in_stock" : "out_of_stock");

  return (
    <PageWrap className="animate-fadeIn">
      <BackLink type="button" onClick={() => navigate("/products")}>
        <ArrowLeft size={16} />
        Back to Products
      </BackLink>

      <HeaderRow>
        <ThumbWrap>
          {imageUrl ? (
            <img src={imageUrl} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Package size={32} color="#9ca3af" />
          )}
        </ThumbWrap>
        <HeaderMain>
          <EntityTitle>{product.name}</EntityTitle>
          <EntityMeta>SKU: {product.sku || "—"}</EntityMeta>
          <StatusBadge $status={statusDisplay}>
            {statusDisplay === "in_stock" ? "In stock" : statusDisplay === "low_stock" ? "Low stock" : "Out of stock"}
          </StatusBadge>
        </HeaderMain>
        <HeaderActions>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
          <Button $variant="outline" onClick={() => navigate("/products", { state: { editProduct: product } })}>
            <Edit size={16} />
            Edit
          </Button>
          <Button $variant="outline" onClick={() => navigate("/stores")}>
            <Package size={16} />
            Sync inventory
          </Button>
        </HeaderActions>
      </HeaderRow>

      <SummaryStrip>
        <SummaryItem>
          <SummaryLabel>Price</SummaryLabel>
          <SummaryValue>{fmt(product.price)}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Sale price</SummaryLabel>
          <SummaryValue>
            {product.salePrice != null && product.salePrice > 0 ? fmt(product.salePrice) : "—"}
          </SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Stock</SummaryLabel>
          <SummaryValue>{product.stock ?? "—"}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Store</SummaryLabel>
          <SummaryValue style={{ fontSize: 14 }}>{product.storeId?.name || product.store || "—"}</SummaryValue>
        </SummaryItem>
      </SummaryStrip>

      <DetailGrid>
        <SectionCard>
          <SectionTitle>Details</SectionTitle>
          <DetailRow>
            <DetailLabel>Category</DetailLabel>
            <DetailValue>{product.category || "—"}</DetailValue>
          </DetailRow>
          {(product.vendor || product.productType) && (
            <>
              {product.vendor && (
                <DetailRow>
                  <DetailLabel>Vendor</DetailLabel>
                  <DetailValue>{product.vendor}</DetailValue>
                </DetailRow>
              )}
              {product.productType && (
                <DetailRow>
                  <DetailLabel>Product type</DetailLabel>
                  <DetailValue>{product.productType}</DetailValue>
                </DetailRow>
              )}
            </>
          )}
          <DetailRow>
            <DetailLabel>Store</DetailLabel>
            <DetailValue>{product.storeId?.name || product.store || "—"}</DetailValue>
          </DetailRow>
          {product.tags && (
            <DetailRow>
              <DetailLabel>Tags</DetailLabel>
              <DetailValue>{product.tags}</DetailValue>
            </DetailRow>
          )}
          {product.weight != null && product.weight > 0 && (
            <DetailRow>
              <DetailLabel>Weight</DetailLabel>
              <DetailValue>{product.weight} kg</DetailValue>
            </DetailRow>
          )}
          {product.dimensions && (
            <DetailRow>
              <DetailLabel>Dimensions</DetailLabel>
              <DetailValue>{product.dimensions}</DetailValue>
            </DetailRow>
          )}
          {(product.shortDescription || product.description) && (
            <DetailRow style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
              <DetailLabel style={{ marginBottom: 4 }}>Description</DetailLabel>
              <DescriptionBlock>
                {product.shortDescription ? `${product.shortDescription}\n\n${product.description || ""}` : (product.description || "—")}
              </DescriptionBlock>
            </DetailRow>
          )}
        </SectionCard>

        <div>
          <SectionCard style={{ marginBottom: 16 }}>
            <SectionTitle>Pricing & inventory</SectionTitle>
            <DetailRow>
              <DetailLabel>Price</DetailLabel>
              <DetailValue>{fmt(product.price)}</DetailValue>
            </DetailRow>
            {product.salePrice != null && product.salePrice > 0 && (
              <DetailRow>
                <DetailLabel>Sale price</DetailLabel>
                <DetailValue style={{ color: "#059669" }}>{fmt(product.salePrice)}</DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>Current stock</DetailLabel>
              <DetailValue>{product.stock ?? "—"}</DetailValue>
            </DetailRow>
          </SectionCard>

          <SectionCard>
            <SectionTitle>Stock management</SectionTitle>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#6b7280" }}>Quantity</label>
              <Input
                type="number"
                min={0}
                value={stockEdit ?? ""}
                onChange={(e) => setStockEdit(e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
                style={{ maxWidth: "100%" }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#6b7280" }}>Status</label>
              <select
                value={statusEdit || "in_stock"}
                onChange={(e) => setStatusEdit(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}
              >
                <option value="in_stock">In stock</option>
                <option value="low_stock">Low stock</option>
                <option value="out_of_stock">Out of stock</option>
              </select>
            </div>
            <Button onClick={handleSaveStock} disabled={savingStock} style={{ width: "100%" }}>
              {savingStock ? "Saving…" : "Save stock"}
            </Button>
            <p style={{ margin: "12px 0 0", fontSize: 11, color: "#9ca3af" }}>
              Configure webhooks in your store to sync stock and price changes automatically.
            </p>
          </SectionCard>
        </div>
      </DetailGrid>

      <Tabs defaultValue="variants">
        <TabsList>
          <TabsTrigger value="variants">Variants</TabsTrigger>
          <TabsTrigger value="attributes">Attributes</TabsTrigger>
          <TabsTrigger value="sales">Sales History</TabsTrigger>
          <TabsTrigger value="gallery">Gallery</TabsTrigger>
          <TabsTrigger value="description">Description</TabsTrigger>
        </TabsList>

        <TabsContent value="variants">
          <Card style={{ marginTop: 16, padding: 16 }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead style={{ textAlign: "right" }}>Stock</TableHead>
                  <TableHead style={{ textAlign: "right" }}>Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variants.length === 0 ? (
                  <TableRow><TableCell colSpan={5} style={{ color: "#6b7280" }}>No variants</TableCell></TableRow>
                ) : (
                  variants.map((variant) => (
                    <TableRow key={variant.id || variant._id}>
                      <TableCell>{variant.sku}</TableCell>
                      <TableCell>{variant.color}</TableCell>
                      <TableCell>{variant.size}</TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        {variant.stock ?? "—"}
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        {variant.price != null ? fmt(variant.price) : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="attributes">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <div
              style={{
                display: "grid",
                gap: 16,
              }}
            >
              {Object.keys(attributes).length === 0 ? (
                <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>No attributes</p>
              ) : Object.entries(attributes).map(([key, value]) => (
                <div key={key}>
                  <h4 style={{ margin: 0, marginBottom: 6, fontWeight: 600 }}>
                    {key}
                  </h4>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {Array.isArray(value) ? (
                      value.map((v) => (
                        <span
                          key={v}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 9999,
                            fontSize: 13,
                            background: "#f3f4f6",
                          }}
                        >
                          {v}
                        </span>
                      ))
                    ) : (
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 9999,
                          fontSize: 13,
                          background: "#f3f4f6",
                        }}
                      >
                        {value}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sales">
          <Card style={{ marginTop: 16, padding: 16 }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead style={{ textAlign: "right" }}>
                    Quantity Sold
                  </TableHead>
                  <TableHead style={{ textAlign: "right" }}>Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesHistory.length === 0 ? (
                  <TableRow><TableCell colSpan={3} style={{ color: "#6b7280" }}>No sales history</TableCell></TableRow>
                ) : salesHistory.map((sale, index) => (
                  <TableRow key={index}>
                    <TableCell>{sale.date}</TableCell>
                    <TableCell style={{ textAlign: "right" }}>
                      {sale.quantity}
                    </TableCell>
                    <TableCell style={{ textAlign: "right" }}>
                      ${sale.revenue}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="gallery">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <h3
              style={{
                margin: 0,
                marginBottom: 8,
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              Product Gallery
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
              No images available in the gallery.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="description">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <h3
              style={{
                margin: 0,
                marginBottom: 8,
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              Product Description
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: "#4b5563" }}>
              {product.description}
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </PageWrap>
  );
}

